import { Box, Stack } from "@mui/material";
import { useCallback, useMemo, useState, type ReactNode } from "react";
import type { ConditionFilterDescriptor } from "../../../../../../../../../api/types/fieldConfigurationTypes.ts";
import type { ConditionField } from "../../../../../../../../../api/types/svTypes.ts";
import { ItemDataType } from "../../../../../../../../../api/types/svTypes.ts";
import {
  getDrillDownConditionLeafFields,
  getDrillDownNarrowingFilters,
  type DrillDownConditionNode,
} from "../../../../../hooks/drillDownConditions.ts";
import ConditionFieldOptionPopup from "../../../../viewer/common/conditions/ConditionFieldOptionPopup.tsx";
import { ConditionItemContainer } from "../../../../viewer/common/conditions/ConditionItemContainer.tsx";
import type { ShowFieldOptionsSettings } from "../../../../viewer/common/Types.ts";
import ConditionTreeRow from "./ConditionTreeRow.tsx";
import DrillDownConditionGroup from "./DrillDownConditionGroup.tsx";

interface Props {
  conditions: DrillDownConditionNode[];
  canBeRemoved: boolean;
  readonly?: boolean;
  onRemoveField?: (field: ConditionField) => void;
  onFieldChanged: (field: ConditionField, changes: Partial<ConditionField>) => void;
}

export default function DrillDownConditionsTree({
  conditions,
  canBeRemoved,
  readonly,
  onRemoveField,
  onFieldChanged,
}: Props) {
  const [optionsAnchorEl, setOptionsAnchorEl] = useState<HTMLElement | null>(null);
  const [optionFieldGuid, setOptionFieldGuid] = useState<string>();
  const [showOnlySelected, setShowOnlySelected] = useState(false);
  const leafFields = useMemo(() => getDrillDownConditionLeafFields(conditions), [conditions]);
  const optionField = useMemo(
    () => leafFields.find((condition) => condition.config.guid === optionFieldGuid),
    [leafFields, optionFieldGuid]
  );
  const narrowingFilters = useMemo(
    () => (optionField === undefined ? [] : getDrillDownNarrowingFilters(conditions, optionField)),
    [conditions, optionField]
  );

  const hasGroupConditions = useMemo(() => {
    return conditions.some((condition) => condition.type === "group" && condition.conditions.length > 1);
  }, [conditions]);

  const handleClosePopup = useCallback(() => {
    setOptionsAnchorEl(null);
    setOptionFieldGuid(undefined);
  }, []);

  const handleShowOptions = useCallback((settings: ShowFieldOptionsSettings<ConditionField>) => {
    setOptionFieldGuid(settings.field.config.guid);
    setOptionsAnchorEl(settings.ref);
    setShowOnlySelected(settings.showOnlySelected);
  }, []);

  const handleApplyFilter = useCallback(
    (filter: ConditionFilterDescriptor) => {
      if (optionField !== undefined) {
        onFieldChanged(optionField, { config: { ...optionField.config, filter } });
      }
      handleClosePopup();
    },
    [handleClosePopup, onFieldChanged, optionField]
  );

  const handleRemoveField = useCallback(() => {
    if (!optionField || !onRemoveField) {
      return;
    }

    onRemoveField(optionField);
    handleClosePopup();
  }, [handleClosePopup, onRemoveField, optionField]);

  const renderNode = (condition: DrillDownConditionNode): ReactNode => {
    if (condition.type === "leaf") {
      return (
        <ConditionItemContainer
          key={condition.id}
          field={condition.field}
          canBeRemoved={canBeRemoved}
          readonly={readonly}
          onRemoveItem={onRemoveField}
          showOptions={handleShowOptions}
        />
      );
    }

    return <DrillDownConditionGroup key={condition.id} condition={condition} renderNode={renderNode} />;
  };

  return (
    <Box
      sx={{
        height: "100%",
        overflow: "hidden",
        width: "100%",
      }}
    >
      <Stack
        spacing={1}
        sx={{
          height: "auto",
          maxHeight: "100%",
          overflowY: "auto",
          overflowX: "hidden",
        }}
      >
        {conditions.map((condition, index) => (
          <ConditionTreeRow
            key={condition.id}
            isFirst={index === 0}
            isLast={index === conditions.length - 1}
            shouldRenderConnector={hasGroupConditions}
          >
            {renderNode(condition)}
          </ConditionTreeRow>
        ))}
        {optionsAnchorEl && optionField && (
          <ConditionFieldOptionPopup
            anchorEl={optionsAnchorEl}
            field={optionField}
            narrowingFilters={narrowingFilters}
            showOnlySelectedByDefault={showOnlySelected}
            canBeRemoved={canBeRemoved}
            readonly={readonly}
            save={handleApplyFilter}
            resizable={optionField.meta.type !== ItemDataType.Date}
            readonlyCustomLabel={true}
            disableReferenceDateSelection
            onRemoveItem={handleRemoveField}
            CustomId={undefined}
          />
        )}
      </Stack>
    </Box>
  );
}
